import { css } from '@emotion/core';
import Link from '@veeva/corgix/link';
import { UnauthenticatedPaths } from '../constants/Paths';
import veevaIdLogo from '../images/veeva-id-logo.svg';
import { isMobile } from '../services/DeviceInformation';
import { getCurrentYear } from '../services/browserUtils';
import useZendesk from '../services/useZendesk';

const LearnMorePage = () => {
    useZendesk();

    const maxWidthCSS = () =>
        css`
            margin: 0 auto;
            max-width: 800px;
        `;

    const headerCSS = () => [
        css`
            font-size: 1.6667rem;
            font-weight: 700;
            margin: 0;
            padding-bottom: 1.6667rem;
        `,
        isMobile() &&
            css`
                text-align: center;
            `,
    ];

    const multiSectionCSS = () => [
        maxWidthCSS(),
        css`
            padding-bottom: 5rem;
        `,
    ];

    const paragraphCSS = () =>
        css`
            margin: 0;
            font-size: 1.3333rem;
            padding-left: 0.6667rem;
        `;

    const multiParagraphCSS = () => [
        paragraphCSS(),
        css`
            padding-bottom: 1.3333rem;
        `,
    ];

    const linkCSS = () =>
        css`
            font-size: 1.3333rem;
        `;

    const gridCSS = () =>
        css`
            font-size: 1.3333rem;
            padding-left: 0.6667rem;
            padding-bottom: 3.3333rem;
            display: grid;
            grid-template-columns: max-content 1fr;
            column-gap: 0.3333rem;

            p {
                margin: 0;
            }
        `;

    return (
        <div>
            <section
                css={[
                    maxWidthCSS(),
                    css`
                        padding: 1.6667rem 3.3333rem 3.3333rem;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                    `,
                ]}
            >
                <Link
                    css={css`
                        padding: 0 0 1.6667rem;
                        & > img {
                            height: 2.5rem;
                        }
                    `}
                    href={window.location.origin + UnauthenticatedPaths.LOGIN}
                    target="_blank"
                    rel="noopener"
                >
                    <img src={veevaIdLogo} alt="VeevaID logo" />
                </Link>
                <section>
                    <h1 css={headerCSS}>
                        One login for you to access your sponsor systems from Veeva
                    </h1>
                    <p
                        css={css`
                            font-size: 1.6667rem;
                            margin: 0;
                        `}
                    >
                        VeevaID reduces the number of logins and works across all Veeva systems such
                        as Veeva SiteVault, Veeva Site Connect, Veeva EDC, Veeva Study Training,
                        Veeva RTSM, Vault PromoMats and Vault Medical.
                    </p>
                </section>
            </section>
            <section
                css={({ lightestGray }) => {
                    return css`
                        padding: 3.3333rem;
                        background-color: ${lightestGray};
                    `;
                }}
            >
                <section css={multiSectionCSS}>
                    <h1 css={headerCSS}>Registering for VeevaID</h1>
                    <p css={paragraphCSS}>
                        VeevaID allows you to self-register for an account. Choose an appropriate,
                        valid email address. There are two ways to register:
                    </p>
                    <ol
                        css={css`
                            margin: 0;
                            font-size: 1.3333rem;
                            padding-left: 2.6667rem;

                            li {
                                padding-top: 1.3333rem;
                            }
                        `}
                    >
                        <li>
                            Self-registration: You can navigate to{' '}
                            <Link
                                css={linkCSS}
                                href={window.location.origin + UnauthenticatedPaths.LOGIN}
                                target="_blank"
                                rel="noopener"
                            >
                                id.veeva.com
                            </Link>{' '}
                            and click <b>Register here</b> to start the self-registration process.
                        </li>
                        <li>
                            Invite from a sponsor or CRO: Vault administrators can invite you to
                            create a VeevaID. Simply follow the instructions via the email invite.
                        </li>
                    </ol>
                </section>
                <section css={multiSectionCSS}>
                    <h1 css={headerCSS}>Logging into VeevaID</h1>
                    <p css={paragraphCSS}>
                        Your email address is your username. The first time you log in, you’ll enter
                        your email address, for example, tibanez@veepharm.com, and your password. On
                        subsequent logins, VeevaID will remember the last email address you used and
                        you’ll only need to enter your password. If you have multiple VeevaID
                        accounts or share a computer with another VeevaID user, you can click{' '}
                        <b>Switch user</b> to enter a different email address.
                    </p>
                </section>
                <section css={multiSectionCSS}>
                    <h1 css={headerCSS}>VeevaID Home</h1>
                    <p css={paragraphCSS}>
                        Upon logging into VeevaID, you are directed to the Home page. It shows a
                        list of all systems to which your VeevaID account has access. Click a{' '}
                        <b>System Name</b> to access that system.
                    </p>
                </section>
                <section css={maxWidthCSS}>
                    <h1 css={headerCSS}>My Account</h1>
                    <p css={multiParagraphCSS}>
                        You can reach the My Account page either by logging in at{' '}
                        <Link
                            css={linkCSS}
                            href={window.location.origin + UnauthenticatedPaths.LOGIN}
                            target="_blank"
                            rel="noopener"
                        >
                            id.veeva.com
                        </Link>{' '}
                        and selecting <b>My Account</b> or by selecting{' '}
                        <b>Go to My VeevaID Account</b> from your Vault User Profile. The My Account
                        page is comprised of three tabs: <i>General</i>, <i>Emails</i>, and{' '}
                        <i>Change Password</i>.
                    </p>
                    <p css={multiParagraphCSS}>
                        The <i>General</i> tab displays the following information about your
                        account:
                    </p>
                    <ul
                        css={css`
                            margin: 0;
                            font-size: 1.3333rem;
                            margin: 0;
                            padding: 0 0 1.3333rem 2.6667rem;

                            li:not(:first-of-type) {
                                padding-top: 1.3333rem;
                            }
                        `}
                    >
                        <li>First Name</li>
                        <li>Last Name</li>
                        <li>Mobile Phone</li>
                        <li>Language</li>
                        <li>Locale</li>
                        <li>Timezone</li>
                    </ul>
                    <p css={multiParagraphCSS}>
                        Select <b>Edit</b> to update this information.
                    </p>
                    <p css={multiParagraphCSS}>
                        The <i>Emails</i> tab displays your Primary Email, which also functions as
                        your VeevaID username.
                    </p>
                    <p css={paragraphCSS}>
                        The <i>Change Password</i> tab allows you to change your VeevaID password.
                        To do so, enter your <b>Current Password</b>, enter and confirm your{' '}
                        <b>New Password</b>, then select <b>Save Your Password</b>.
                    </p>
                </section>
            </section>
            <section
                css={[
                    maxWidthCSS(),
                    css`
                        padding: 3.3333rem 3.3333rem 6.6667rem;
                    `,
                ]}
            >
                <h1 css={headerCSS}>FAQs:</h1>
                <section css={gridCSS}>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        Q:
                    </b>
                    <b>What if I have a VeevaID account and cannot log in?</b>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        A:
                    </b>
                    <p>
                        Try to{' '}
                        <Link
                            css={linkCSS}
                            href={window.location.origin + UnauthenticatedPaths.FORGOT_PASSWORD}
                            target="_blank"
                            rel="noopener"
                        >
                            reset your password
                        </Link>
                        .
                    </p>
                </section>
                <section css={gridCSS}>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        Q:
                    </b>
                    <b>My account is locked, what do I do?</b>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        A:
                    </b>
                    <p>
                        Wait 12 hours for your account to be unlocked or reset your password to
                        unlock your account immediately.
                    </p>
                </section>
                <section css={gridCSS}>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        Q:
                    </b>
                    <b>What is our password policy?</b>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        A:
                    </b>
                    <p
                        css={css`
                            line-height: 1.3333rem;

                            p {
                                padding-top: 1.3333rem;
                            }
                        `}
                    >
                        Your password must have a minimum length of 8 characters.
                        <p>Your password must have one or more lowercase letter.</p>
                        <p>Your password must have one or more uppercase letter.</p>
                        <p>Your password must have one or more numbers.</p>
                        <p>
                            Your password shall not be able to set a password that is on a list of
                            known weak passwords or a password too commonly used by other users in
                            the system.
                        </p>
                        <p>
                            You shall be prompted for a four-digit verification code when you login
                            for the first time, or login on a new device.
                        </p>
                    </p>
                </section>
                <section css={gridCSS}>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        Q:
                    </b>
                    <b>When is multi-factor authentication required?</b>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        A:
                    </b>
                    <p>
                        When you login for the first time, login on a new device, and once every 30
                        days.
                    </p>
                </section>
                <section css={gridCSS}>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        Q:
                    </b>
                    <b>When will I need to login again if I&apos;m inactive?</b>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        A:
                    </b>
                    <p>
                        If you are inactive on{' '}
                        <Link
                            css={linkCSS}
                            href={window.location.origin + UnauthenticatedPaths.LOGIN}
                            target="_blank"
                            rel="noopener"
                        >
                            id.veeva.com
                        </Link>{' '}
                        for 30 minutes, you will be required sign-in again to continue. If you are
                        working directly in a Vault that you have accessed using your VeevaID, the
                        timeout will vary based on that Vault&apos;s specific settings. If you use
                        the Log Out option from within a Vault, this will also log you out of
                        VeevaID.
                    </p>
                </section>
                <section css={gridCSS}>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        Q:
                    </b>
                    <b>Is VeevaID free?</b>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        A:
                    </b>
                    <p>Yes.</p>
                </section>
                <section css={gridCSS}>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        Q:
                    </b>
                    <b>When does my email invite expire?</b>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        A:
                    </b>
                    <p>
                        Your email invite will expire in two weeks. After your invite expires, If
                        you would like to register go to id.veeva.com and self-register or ask the
                        Vault administrator to resend the email invitation.
                    </p>
                </section>
                <section css={gridCSS}>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        Q:
                    </b>
                    <b>What if my email address changes?</b>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        A:
                    </b>
                    <p>
                        Your VeevaID is tied to an email address. If your email address changes,
                        please create a new VeevaID account and ask your system administrator(s) to
                        grant you access to your system(s).
                    </p>
                </section>
                <section css={gridCSS}>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        Q:
                    </b>
                    <b>I am a Vault administrator, where do I go to learn more?</b>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        A:
                    </b>
                    <p>
                        You can learn more at{' '}
                        <Link
                            css={linkCSS}
                            href="https://platform.veevavault.help/en/lr/23050"
                            target="_blank"
                            rel="noopener"
                        >
                            Vault Help
                        </Link>
                        .
                    </p>
                </section>
                <section
                    css={() => [
                        gridCSS(),
                        css`
                            padding-bottom: 0;
                        `,
                    ]}
                >
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        Q:
                    </b>
                    <b>I am a partner, where do I go to learn more?</b>
                    <b
                        css={css`
                            text-align: right;
                        `}
                    >
                        A:
                    </b>
                    <p>
                        You can learn more at{' '}
                        <Link
                            css={linkCSS}
                            href="https://www.veeva.com/meet-veeva/partners/veeva-id/"
                            target="_blank"
                            rel="noopener"
                        >
                            VeevaID Partner Program
                        </Link>
                        .
                    </p>
                </section>
            </section>
            <footer
                css={[
                    maxWidthCSS(),
                    css`
                        padding: 0 3.3333rem 1.6667rem;
                        font-size: 1.3333rem;
                        text-align: center;
                    `,
                ]}
            >
                ©{getCurrentYear()} Veeva Systems Inc. All rights reserved. | Contact Us at{' '}
                <a href="mailto:VeevaID.Support@veeva.com">VeevaID.Support@veeva.com</a>
            </footer>
        </div>
    );
};

export default LearnMorePage;
